
import { useEffect, useState } from 'react';
import { useScrollFadeIn } from '../../hooks/useScrollFadeIn';

function FadeInSection({ children, className = '', animation = 'scroll-fade-in-up', delay = '' }: {
  children: React.ReactNode;
  className?: string;
  animation?: string;
  delay?: string;
}) {
  const { ref, isVisible } = useScrollFadeIn();
  return (
    <div
      ref={ref}
      className={`${className} ${isVisible ? `${animation} ${delay}` : 'scroll-hidden'}`}
    >
      {children}
    </div>
  );
}

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-sans">
      {/* Navigation */}
      <header>
        <nav
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
          }`}
          aria-label="メインナビゲーション"
        >
          <div className="px-6 lg:px-20 h-16 flex items-center justify-between">
            <h1>
              <a
                href="#hero"
                onClick={(e) => { e.preventDefault(); scrollToSection('hero'); }}
                className={`text-xl font-semibold tracking-wider transition-colors cursor-pointer ${
                  isScrolled ? 'text-[#2B2B2B]' : 'text-white'
                }`}
                title="株式会社LETS - 日常価格でちょっと感動する居酒屋"
              >
                LETS
              </a>
            </h1>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
              aria-label="メニューを開く"
              aria-expanded={isMenuOpen}
            >
              <span className={`w-6 h-0.5 transition-all ${isScrolled ? 'bg-[#2B2B2B]' : 'bg-white'} ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`w-6 h-0.5 transition-all ${isScrolled ? 'bg-[#2B2B2B]' : 'bg-white'} ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`w-6 h-0.5 transition-all ${isScrolled ? 'bg-[#2B2B2B]' : 'bg-white'} ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="absolute top-16 left-0 right-0 bg-white/98 backdrop-blur-md shadow-lg">
              <div className="px-6 py-8 flex flex-col gap-4">
                <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="text-left text-base text-[#2B2B2B] hover:text-[#D4856A] transition-colors cursor-pointer whitespace-nowrap">
                  ABOUT LETS
                </a>
                <a href="#philosophy" onClick={(e) => { e.preventDefault(); scrollToSection('philosophy'); }} className="text-left text-base text-[#2B2B2B] hover:text-[#D4856A] transition-colors cursor-pointer whitespace-nowrap">
                  私たちの考え方
                </a>
                <a href="#stores" onClick={(e) => { e.preventDefault(); scrollToSection('stores'); }} className="text-left text-base text-[#2B2B2B] hover:text-[#D4856A] transition-colors cursor-pointer whitespace-nowrap">
                  OUR STORE
                </a>
                <a href="#company" onClick={(e) => { e.preventDefault(); scrollToSection('company'); }} className="text-left text-base text-[#2B2B2B] hover:text-[#D4856A] transition-colors cursor-pointer whitespace-nowrap">
                  会社概要
                </a>
              </div>
            </div>
          )}
        </nav>
      </header>

      <main>
        {/* Hero Section */}
        <section id="hero" className="relative h-screen flex items-center justify-center overflow-hidden" aria-label="メインビジュアル">
          <div className="absolute inset-0">
            <div className="w-full h-full overflow-hidden">
              <img
                src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/361137aa179adac99689bd5738f05e3c.jpeg"
                alt="株式会社LETS運営の居酒屋店内の温かな雰囲気"
                title="LETS居酒屋 - 日常価格でちょっと感動する空間"
                className="w-full h-full object-cover animate-subtle-zoom"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
          </div>

          {/* Top Navigation Links */}
          <div className="absolute top-20 left-0 right-0 z-10 px-6 lg:px-20">
            <nav className="flex flex-wrap items-center justify-center gap-6 lg:gap-10" aria-label="セクションナビゲーション">
              <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="text-sm lg:text-base text-white/90 hover:text-white transition-colors cursor-pointer whitespace-nowrap font-medium">
                ABOUT LETS
              </a>
              <a href="#philosophy" onClick={(e) => { e.preventDefault(); scrollToSection('philosophy'); }} className="text-sm lg:text-base text-white/90 hover:text-white transition-colors cursor-pointer whitespace-nowrap font-medium">
                私たちの考え方
              </a>
              <a href="#stores" onClick={(e) => { e.preventDefault(); scrollToSection('stores'); }} className="text-sm lg:text-base text-white/90 hover:text-white transition-colors cursor-pointer whitespace-nowrap font-medium">
                OUR STORE
              </a>
              <a href="#company" onClick={(e) => { e.preventDefault(); scrollToSection('company'); }} className="text-sm lg:text-base text-white/90 hover:text-white transition-colors cursor-pointer whitespace-nowrap font-medium">
                会社概要
              </a>
            </nav>
          </div>

          <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-wide mb-4 animate-fade-in-up">
              日常価格で、ちょっと感動する店。
            </h2>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-medium text-white/95 mb-8 animate-fade-in-up-delay-1">
              Let&apos;s do it ─ まずやってみよう！
            </p>
            <p className="text-sm sm:text-base text-white/85 font-normal mb-12 animate-fade-in-up-delay-2">
              ふらっと来られて、ちゃんと美味しい。<br />
              そんな&quot;ちょうどいい感動&quot;を増やしていく。
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up-delay-3">
              <a
                href="#stores"
                onClick={(e) => { e.preventDefault(); scrollToSection('stores'); }}
                className="px-8 py-3.5 bg-white/10 backdrop-blur-sm text-white border border-white/30 rounded-full hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm sm:text-base font-medium"
              >
                店舗一覧を見る
              </a>
              <a
                href="#philosophy"
                onClick={(e) => { e.preventDefault(); scrollToSection('philosophy'); }}
                className="px-8 py-3.5 bg-white text-[#2B2B2B] rounded-full hover:bg-white/90 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm sm:text-base font-medium"
              >
                私たちの考え方
              </a>
            </div>
          </div>

          <a
            href="#gallery"
            onClick={(e) => { e.preventDefault(); scrollToSection('gallery'); }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2 w-10 h-10 flex items-center justify-center cursor-pointer animate-bounce"
            aria-label="ギャラリーセクションへスクロール"
          >
            <i className="ri-arrow-down-line text-2xl text-white"></i>
          </a>
        </section>

        {/* Gallery Section */}
        <section id="gallery" className="bg-white py-16 lg:py-20 overflow-hidden" aria-label="店舗ギャラリー">
          <div 
            className="relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
          >
            <div className="flex gap-6" style={{
              animation: isPaused ? 'none' : 'scroll-left 60s linear infinite',
              width: 'fit-content'
            }}>
              {/* First set of images */}
              <div className="flex gap-6">
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/de76f0c9d2b5edf812132bfe54faa894.jpeg" alt="LETS居酒屋の落ち着いた店内雰囲気" title="居酒屋リンカーン・酒場ロッカクの店内" className="w-full h-full object-cover" />
                </div>
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/14ad90e58245efb05922915671bfb4ec.jpeg" alt="日常価格で楽しめる美味しい料理の一例" title="LETS居酒屋のこだわり料理" className="w-full h-full object-cover" />
                </div>
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/94b302c0ef1765b31ccbf9db2d01305b.jpeg" alt="居酒屋リンカーンの落ち着いた空間" title="東京・神奈川の居酒屋LETS" className="w-full h-full object-cover" />
                </div>
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/ae5305e5c2626d93a8c5116a2129c0c5.jpeg" alt="酒場ロッカクの豊富なお酒の品揃え" title="練馬の洋風酒場ロッカク" className="w-full h-full object-cover" />
                </div>
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/cb1a0f19dbc6ed6812c64dc659803d98.jpeg" alt="居酒屋のカウンター席で楽しむひととき" title="LETS居酒屋のカウンター席" className="w-full h-full object-cover" />
                </div>
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/368ecc9774be6a6e81e00c91d59883d2.jpeg" alt="スタッフがこだわって作る料理" title="日常価格で感動する料理" className="w-full h-full object-cover" />
                </div>
              </div>

              {/* Duplicate set for seamless loop */}
              <div className="flex gap-6">
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/de76f0c9d2b5edf812132bfe54faa894.jpeg" alt="LETS居酒屋の落ち着いた店内雰囲気" title="居酒屋リンカーン・酒場ロッカクの店内" className="w-full h-full object-cover" />
                </div>
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/14ad90e58245efb05922915671bfb4ec.jpeg" alt="日常価格で楽しめる美味しい料理の一例" title="LETS居酒屋のこだわり料理" className="w-full h-full object-cover" />
                </div>
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/94b302c0ef1765b31ccbf9db2d01305b.jpeg" alt="居酒屋リンカーンの落ち着いた空間" title="東京・神奈川の居酒屋LETS" className="w-full h-full object-cover" />
                </div>
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/ae5305e5c2626d93a8c5116a2129c0c5.jpeg" alt="酒場ロッカクの豊富なお酒の品揃え" title="練馬の洋風酒場ロッカク" className="w-full h-full object-cover" />
                </div>
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/cb1a0f19dbc6ed6812c64dc659803d98.jpeg" alt="居酒屋のカウンター席で楽しむひととき" title="LETS居酒屋のカウンター席" className="w-full h-full object-cover" />
                </div>
                <div className="w-80 h-64 flex-shrink-0 rounded-2xl overflow-hidden">
                  <img src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/368ecc9774be6a6e81e00c91d59883d2.jpeg" alt="スタッフがこだわって作る料理" title="日常価格で感動する料理" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About LETS Section */}
        <section id="about" className="bg-[#F7F4F0] py-20 lg:py-32 px-6 lg:px-20" aria-labelledby="about-heading">
          <article className="max-w-4xl mx-auto">
            <FadeInSection className="text-center mb-12 lg:mb-16">
              <h4 id="about-heading" className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2B2B2B] mb-4">
                <a href="#about" className="hover:text-[#D4856A] transition-colors">ABOUT LETS</a>
              </h4>
              <div className="w-16 h-0.5 bg-[#D4856A] mx-auto"></div>
            </FadeInSection>

            <FadeInSection className="text-center mb-16 lg:mb-20" delay="scroll-delay-1">
              <p className="text-base sm:text-lg lg:text-xl text-[#2B2B2B] leading-relaxed mb-8">
                <strong>LETS</strong>は、<strong>飲食店</strong>の運営を通じて、<br className="hidden sm:block" />
                街に「ちょうどいい夜」を増やしていく会社です。
              </p>
              <p className="text-sm sm:text-base lg:text-lg text-[#5A5A5A] leading-relaxed mb-6">
                私たちが大切にしているのは、<br />
                特別すぎないこと。<br />
                でも、記憶にはちゃんと残ること。
              </p>
              <p className="text-sm sm:text-base lg:text-lg text-[#5A5A5A] leading-relaxed">
                ふらっと入れて、値段も気負わない。<br />
                それなのに<br />
                「今日、なんか良かったな」<br />
                そう思ってもらえる時間を、一つひとつ積み重ねたいと考えています。
              </p>
            </FadeInSection>
          </article>
        </section>

        {/* Philosophy Section */}
        <section id="philosophy" className="bg-white py-20 lg:py-32 px-6 lg:px-20" aria-labelledby="philosophy-heading">
          <div className="max-w-6xl mx-auto">
            <FadeInSection className="text-center mb-16">
              <h4 id="philosophy-heading" className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2B2B2B] mb-4">
                <a href="#philosophy" className="hover:text-[#D4856A] transition-colors">私たちの考え方</a>
              </h4>
              <div className="w-16 h-0.5 bg-[#D4856A] mx-auto"></div>
            </FadeInSection>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
              {/* Card 1 */}
              <FadeInSection animation="scroll-fade-in-left">
                <article className="bg-[#FAFAF8] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="relative h-56 w-full">
                    <img
                      src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/94b302c0ef1765b31ccbf9db2d01305b.jpeg"
                      alt="気軽に入れる居酒屋リンカーンの雰囲気"
                      title="日常価格でちょっと感動する居酒屋体験"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8 lg:p-10">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4">
                      <i className="ri-arrow-right-up-line text-2xl text-[#D4856A]"></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-semibold text-[#2B2B2B] mb-5 leading-snug">
                      Let&apos;s go<br />気軽さの中に、ちゃんとした感動を
                    </h3>
                    <p className="text-sm sm:text-base text-[#5A5A5A] leading-relaxed">
                      私たちが目指しているのは、「いつでもふらっと来られる価格」なのに「ここでしか味わえない体験」があるお店です。<strong>お手頃な価格帯</strong>でありながら、「え、これ美味しい」「ちょっと感動した」そんな気持ちが自然に生まれる時間を提供することを、何よりも大切にしています。
                    </p>
                  </div>
                </article>
              </FadeInSection>

              {/* Card 2 */}
              <FadeInSection animation="scroll-fade-in-right">
                <article className="bg-[#FAFAF8] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="relative h-56 w-full">
                    <img
                      src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/ae5305e5c2626d93a8c5116a2129c0c5.jpeg"
                      alt="酒場ロッカクのスタッフが協力し合う様子"
                      title="現場スタッフの想いを大切にする飲食店"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8 lg:p-10">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4">
                      <i className="ri-user-heart-line text-2xl text-[#D4856A]"></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-semibold text-[#2B2B2B] mb-5 leading-snug">
                      主役は&quot;会社&quot;ではなく<br />&quot;現場のスタッフ&quot;
                    </h3>
                    <p className="text-sm sm:text-base text-[#5A5A5A] leading-relaxed">
                      実際に店舗に立つ社員一人ひとりの「こうしたい」を大切にしています。「こんなメニューを出してみたい」「こんなイベントをやってみたい」そんな<strong>現場の声</strong>をどう形にしていくかを、会社として本気で考えます。お店の空気をつくるのは、そこに働く&quot;人の想い&quot;だと信じているからです。
                    </p>
                  </div>
                </article>
              </FadeInSection>

              {/* Card 3 */}
              <FadeInSection animation="scroll-fade-in-left">
                <article className="bg-[#FAFAF8] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="relative h-56 w-full">
                    <img
                      src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/7b661994af6a1c7b5249b5f34d741872.jpeg"
                      alt="新しいメニュー開発に取り組むLETSスタッフ"
                      title="Let's do it - まずやってみようの精神"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8 lg:p-10">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4">
                      <i className="ri-lightbulb-line text-2xl text-[#D4856A]"></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-semibold text-[#2B2B2B] mb-5 leading-snug">
                      Let&apos;s do it の精神<br />まずやってみよう
                    </h3>
                    <p className="text-sm sm:text-base text-[#5A5A5A] leading-relaxed">
                      私たちの根底にあるのは、「なんでも一度やってみよう」という姿勢です。失敗しないことよりも、<strong>挑戦しないことの方がもったいない</strong>。新しいメニュー、新しい接客、新しい取り組み。チャレンジを続けることで、会社もスタッフも成長していく。その積み重ねが、お店の魅力になっていくと考えています。
                    </p>
                  </div>
                </article>
              </FadeInSection>

              {/* Card 4 */}
              <FadeInSection animation="scroll-fade-in-right">
                <article className="bg-[#FAFAF8] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="relative h-56 w-full">
                    <img
                      src="https://public.readdy.ai/ai/img_res/3bf839a27e04c84c26e87c20b93b8968.jpg"
                      alt="居酒屋でお客様が笑顔で満足している瞬間"
                      title="目の前のありがとうを大切にする飲食店"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8 lg:p-10">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4">
                      <i className="ri-heart-3-line text-2xl text-[#D4856A]"></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-semibold text-[#2B2B2B] mb-5 leading-snug">
                      目の前の「ありがとう」を大切に
                    </h3>
                    <p className="text-sm sm:text-base text-[#5A5A5A] leading-relaxed">
                      私たちが大事にしているのは、何年後の評価よりも、<strong>目の前のお客様の反応</strong>です。料理を出した瞬間の表情、「美味しい！」の一言、帰り際の「また来ますね」。その場で生まれる感謝や楽しさこそが、私たちの原動力です。お客様にとって&quot;今この瞬間が楽しい店&quot;であり続けたいと思っています。
                    </p>
                  </div>
                </article>
              </FadeInSection>
            </div>
          </div>
        </section>

        {/* Stores Section */}
        <section id="stores" className="bg-[#F0EDE8] py-20 lg:py-32 px-6 lg:px-20" aria-labelledby="stores-heading">
          <div className="max-w-7xl mx-auto">
            <FadeInSection className="text-center mb-12 lg:mb-16">
              <h4 id="stores-heading" className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2B2B2B] mb-4">
                <a href="#stores" className="hover:text-[#D4856A] transition-colors">OUR STORE</a>
              </h4>
              <div className="w-16 h-0.5 bg-[#D4856A] mx-auto"></div>
            </FadeInSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {/* Store 1 - 居酒屋リンカーン 平塚店 */}
              <FadeInSection animation="scroll-fade-in-scale" delay="scroll-delay-1">
                <article className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <div className="relative h-72 w-full">
                    <img
                      src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/dfe59ccfe4d6e109b974067fdccfaa6b.jpeg"
                      alt="居酒屋リンカーン平塚店の温かな店内"
                      title="平塚の居酒屋リンカーン - 地方に愛される居場所"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full">
                      <span className="text-xs font-medium text-[#2B2B2B]">OPEN 2023.10</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl sm:text-2xl font-semibold text-[#2B2B2B] mb-3">
                      <strong>居酒屋リンカーン</strong> 平塚店
                    </h3>
                    <p className="text-sm sm:text-base text-[#8B8680] leading-relaxed mb-4">
                      地方に愛される、温かな居場所
                    </p>
                    <div className="flex items-center gap-3">
                      <a
                        href="https://tabelog.com/kanagawa/A1404/A140407/14093216/"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4856A] text-white rounded-full hover:bg-[#C17559] transition-all duration-300 cursor-pointer whitespace-nowrap text-sm font-medium"
                      >
                        <i className="ri-restaurant-line text-base"></i>
                        食べログで見る
                      </a>
                      <a
                        href="https://www.instagram.com/lincoln_hiratsuka/"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white rounded-full hover:opacity-80 transition-all duration-300 cursor-pointer"
                        aria-label="居酒屋リンカーン平塚店のInstagram"
                      >
                        <i className="ri-instagram-line text-lg"></i>
                      </a>
                    </div>
                  </div>
                </article>
              </FadeInSection>

              {/* Store 2 - 酒場ロッカク 練馬店 */}
              <FadeInSection animation="scroll-fade-in-scale" delay="scroll-delay-2">
                <article className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <div className="relative h-72 w-full">
                    <img
                      src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/f5899189b4da06d6266fc9f9650d6c34.jpeg"
                      alt="酒場ロッカク練馬店の洋風な店内"
                      title="練馬の酒場ロッカク - 心地よい洋風酒場"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full">
                      <span className="text-xs font-medium text-[#2B2B2B]">OPEN 2024.06</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl sm:text-2xl font-semibold text-[#2B2B2B] mb-3">
                      <strong>酒場ロッカク</strong> 練馬店
                    </h3>
                    <p className="text-sm sm:text-base text-[#8B8680] leading-relaxed mb-4">
                      日常に寄り添う、心地よい洋風酒場
                    </p>
                    <div className="flex items-center gap-3">
                      <a
                        href="https://tabelog.com/tokyo/A1321/A132102/13297089/"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4856A] text-white rounded-full hover:bg-[#C17559] transition-all duration-300 cursor-pointer whitespace-nowrap text-sm font-medium"
                      >
                        <i className="ri-restaurant-line text-base"></i>
                        食べログで見る
                      </a>
                      <a
                        href="https://www.instagram.com/rokkaku_nerima/"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white rounded-full hover:opacity-80 transition-all duration-300 cursor-pointer"
                        aria-label="酒場ロッカク練馬店のInstagram"
                      >
                        <i className="ri-instagram-line text-lg"></i>
                      </a>
                    </div>
                  </div>
                </article>
              </FadeInSection>

              {/* Store 3 - 居酒屋リンカーン 町田店 */}
              <FadeInSection animation="scroll-fade-in-scale" delay="scroll-delay-3">
                <article className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <div className="relative h-72 w-full">
                    <img
                      src="https://static.readdy.ai/image/019b42fff89d4ab3f5c2e71398608386/14beffdef4e79699696e3b72e3381072.jpeg"
                      alt="居酒屋リンカーン町田店の活気ある店内"
                      title="町田の居酒屋リンカーン - 海鮮酒場"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full">
                      <span className="text-xs font-medium text-[#2B2B2B]">OPEN 2024.11</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl sm:text-2xl font-semibold text-[#2B2B2B] mb-3">
                      <strong>居酒屋リンカーン</strong> 町田店
                    </h3>
                    <p className="text-sm sm:text-base text-[#8B8680] leading-relaxed mb-4">
                      楽しい夜が生まれる町田の海鮮酒場
                    </p>
                    <div className="flex items-center gap-3">
                      <a
                        href="https://tabelog.com/tokyo/A1327/A132701/13307626/"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4856A] text-white rounded-full hover:bg-[#C17559] transition-all duration-300 cursor-pointer whitespace-nowrap text-sm font-medium"
                      >
                        <i className="ri-restaurant-line text-base"></i>
                        食べログで見る
                      </a>
                      <a
                        href="https://www.instagram.com/lincoln_machida/"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white rounded-full hover:opacity-80 transition-all duration-300 cursor-pointer"
                        aria-label="居酒屋リンカーン町田店のInstagram"
                      >
                        <i className="ri-instagram-line text-lg"></i>
                      </a>
                    </div>
                  </div>
                </article>
              </FadeInSection>
            </div>
          </div>
        </section>

        {/* Company Info Section */}
        <section id="company" className="bg-white py-20 lg:py-28 px-6 lg:px-20" aria-labelledby="company-heading">
          <div className="max-w-4xl mx-auto">
            <FadeInSection className="text-center mb-12 lg:mb-16">
              <h4 id="company-heading" className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2B2B2B] mb-4">
                <a href="#company" className="hover:text-[#D4856A] transition-colors">会社概要</a>
              </h4>
              <div className="w-16 h-0.5 bg-[#D4856A] mx-auto"></div>
            </FadeInSection>

            <FadeInSection>
              <dl className="space-y-0">
                <div className="flex flex-col sm:flex-row py-5 border-b border-[#E8E6E3]">
                  <dt className="w-full sm:w-1/4 mb-2 sm:mb-0">
                    <span className="text-sm font-semibold text-[#8B8680]">会社名</span>
                  </dt>
                  <dd className="w-full sm:w-3/4">
                    <span className="text-base text-[#2B2B2B]">株式会社LETS</span>
                  </dd>
                </div>

                <div className="flex flex-col sm:flex-row py-5 border-b border-[#E8E6E3]">
                  <dt className="w-full sm:w-1/4 mb-2 sm:mb-0">
                    <span className="text-sm font-semibold text-[#8B8680]">代表者名</span>
                  </dt>
                  <dd className="w-full sm:w-3/4">
                    <span className="text-base text-[#2B2B2B]">内部充広</span>
                  </dd>
                </div>

                <div className="flex flex-col sm:flex-row py-5 border-b border-[#E8E6E3]">
                  <dt className="w-full sm:w-1/4 mb-2 sm:mb-0">
                    <span className="text-sm font-semibold text-[#8B8680]">所在地</span>
                  </dt>
                  <dd className="w-full sm:w-3/4">
                    <span className="text-base text-[#2B2B2B]">〒154-0016 東京都世田谷区弦巻5-20-14</span>
                  </dd>
                </div>

                <div className="flex flex-col sm:flex-row py-5 border-b border-[#E8E6E3]">
                  <dt className="w-full sm:w-1/4 mb-2 sm:mb-0">
                    <span className="text-sm font-semibold text-[#8B8680]">資本金</span>
                  </dt>
                  <dd className="w-full sm:w-3/4">
                    <span className="text-base text-[#2B2B2B]">1,000,000円</span>
                  </dd>
                </div>

                <div className="flex flex-col sm:flex-row py-5 border-b border-[#E8E6E3]">
                  <dt className="w-full sm:w-1/4 mb-2 sm:mb-0">
                    <span className="text-sm font-semibold text-[#8B8680]">事業内容</span>
                  </dt>
                  <dd className="w-full sm:w-3/4">
                    <span className="text-base text-[#2B2B2B]">飲食店の運営（居酒屋リンカーン・酒場ロッカク）</span>
                  </dd>
                </div>
              </dl>
            </FadeInSection>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#D4856A] pt-16 lg:pt-24 pb-10">
        <div className="px-6 lg:px-20 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-base sm:text-lg lg:text-xl text-white leading-relaxed mb-8">
              街に愛され根ざした店を、ひとつひとつ。
            </p>
            <h3 className="text-lg font-semibold text-white mb-3">株式会社LETS</h3>
            <p className="text-sm text-white/80 mb-8">〒154-0016 東京都世田谷区弦巻5-20-14</p>
            
            {/* Footer Navigation Links */}
            <nav className="flex flex-wrap items-center justify-center gap-6 lg:gap-8 mb-8" aria-label="フッターナビゲーション">
              <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                ABOUT LETS
              </a>
              <a href="#philosophy" onClick={(e) => { e.preventDefault(); scrollToSection('philosophy'); }} className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                私たちの考え方
              </a>
              <a href="#stores" onClick={(e) => { e.preventDefault(); scrollToSection('stores'); }} className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                OUR STORE
              </a>
              <a href="#company" onClick={(e) => { e.preventDefault(); scrollToSection('company'); }} className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                会社概要
              </a>
            </nav>
          </div>
        </div>

        <div className="bg-[#C17559] py-6 mt-8">
          <div className="px-6 text-center">
            <p className="text-xs text-white/90">&copy; 2025 LETS Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
